import { Box, Typography } from '@mui/material';
import type { ReactNode } from 'react';
import Logo from '../ui/Logo';

/**
 * Branded, full-viewport backdrop for the login / signup screens:
 * warm dotfield texture, ambient teal + clay blobs, and a centered glass card.
 */
export default function AuthShell({
  title,
  subtitle,
  children,
  footer,
}: {
  title: string;
  subtitle: string;
  children: ReactNode;
  footer?: ReactNode;
}) {
  return (
    <Box
      sx={{
        position: 'relative',
        minHeight: '100vh',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        px: 2,
        py: 6,
        overflow: 'hidden',
      }}
    >
      {/* Ambient background */}
      <Box className="bg-dotfield" sx={{ position: 'absolute', inset: 0 }} />
      <Box
        className="blob animate-float-slow"
        sx={{ width: 420, height: 420, top: -120, left: -80, background: 'var(--color-teal)' }}
      />
      <Box
        className="blob animate-float-slower"
        sx={{ width: 360, height: 360, bottom: -140, right: -90, background: 'var(--color-clay)' }}
      />

      {/* Card */}
      <Box
        className="glass-card"
        sx={{
          position: 'relative',
          width: '100%',
          maxWidth: 420,
          p: { xs: 3.5, sm: 4.5 },
          borderRadius: 'var(--radius-xl)',
          boxShadow: 'var(--shadow-e3)',
        }}
      >
        <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', mb: 3 }}>
          <Box
            sx={{
              borderRadius: '14px',
              lineHeight: 0,
              boxShadow: '0 10px 28px -10px color-mix(in oklab, var(--color-primary) 55%, transparent)',
              mb: 2,
            }}
          >
            <Logo size={56} />
          </Box>
          <Typography
            align="center"
            className="font-display text-accent-gradient"
            sx={{ fontWeight: 800, fontSize: '1.55rem', letterSpacing: '-0.025em' }}
          >
            {title}
          </Typography>
          <Typography align="center" variant="body2" sx={{ color: 'var(--color-muted)', mt: 0.75 }}>
            {subtitle}
          </Typography>
        </Box>

        {children}

        {footer && <Box sx={{ mt: 3, textAlign: 'center' }}>{footer}</Box>}
      </Box>
    </Box>
  );
}
